import { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { Loader2, CheckCircle2, FileText, Unlock, Trash2, Filter, Download, Edit3, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminDiary() {
  const { api } = useTenant();
  const [resp, setResp] = useState({ data: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", from: "", to: "" });
  const [marking, setMarking] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.from) params.set("from_date", filters.from);
    if (filters.to)   params.set("to_date", filters.to);
    api.get(`/admin/diary?${params}`)
      .then(r => setResp(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filters]); // eslint-disable-line

  const onUnlock = async (e) => {
    if (!window.confirm(`Unlock entry ${e.invoice_number || ""} so operator can edit again?`)) return;
    try { await api.post(`/admin/diary/${e.id}/unlock`); load(); toast.success("Unlocked"); }
    catch (err) { toast.error(err.response?.data?.detail || "Unlock failed"); }
  };
  const onDelete = async (e) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try { await api.delete(`/admin/diary/${e.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const exportCSV = () => {
    const rows = [["Date","Operator","Customer","Machine","Field","Task","Acres","Hours","Fuel L","Invoice #","Status","Notes"]];
    for (const e of resp.data) {
      rows.push([e.work_date, e.operator_name, e.client_name, e.machine_label, e.field_name, e.task, e.acres, e.hours, e.fuel_litres, e.invoice_number || "", e.status, e.notes || ""]);
    }
    const csv = rows.map(r => r.map(c => `"${(c == null ? "" : String(c)).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `diary_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold mb-1">Work Diary</h1>
          <p className="text-sm text-slate-400">All entries logged by operators in the iPad app. Filter, review, and mark as invoiced.</p>
        </div>
        <button onClick={exportCSV} data-testid="export-csv"
                className="bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3">
        <Filter size={16} className="text-slate-400 mb-2" />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Status</label>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="filter-status"
                  className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="invoiced">Invoiced</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">From</label>
          <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} data-testid="filter-from"
                 className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">To</label>
          <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} data-testid="filter-to"
                 className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={() => setFilters({ status: "", from: "", to: "" })} className="text-slate-400 hover:text-amber-300 text-sm ml-2">Clear</button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {[
          { l: "Entries",     v: resp.totals.count || 0 },
          { l: "Draft",       v: resp.totals.draft || 0 },
          { l: "Invoiced",    v: resp.totals.invoiced || 0 },
          { l: "Total acres", v: (resp.totals.acres || 0).toFixed(1) },
          { l: "Total hours", v: (resp.totals.hours || 0).toFixed(1) },
        ].map(s => (
          <div key={s.l} className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4 text-center">
            <div className="text-2xl font-display font-black text-amber-300 tabular-nums">{s.v}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Operator</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Machine / Field</th>
                <th className="text-left px-3 py-3">Task</th>
                <th className="text-right px-3 py-3">Acres</th>
                <th className="text-right px-3 py-3">Hours</th>
                <th className="text-right px-3 py-3">Fuel</th>
                <th className="text-left px-3 py-3">Invoice</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {resp.data.map(e => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-${e.id}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">{e.work_date}</td>
                  <td className="px-3 py-2.5 text-slate-300">{e.operator_name}</td>
                  <td className="px-3 py-2.5 font-medium">{e.client_name || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-300">
                    <div className="truncate max-w-[180px]">{e.machine_label || "—"}</div>
                    {e.field_name && <div className="text-[11px] text-slate-500">{e.field_name}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-300">{e.task || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-300">{e.acres ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-300">{e.hours ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-300">{e.fuel_litres ?? "—"}</td>
                  <td className="px-3 py-2.5">
                    {e.status === "invoiced" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                        <CheckCircle2 size={10} /> {e.invoice_number || "—"}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">Draft</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {e.status === "invoiced" ? (
                      <button onClick={() => onUnlock(e)} title="Unlock"
                              className="text-slate-300 hover:text-amber-300 p-1.5"><Unlock size={14} /></button>
                    ) : (
                      <button onClick={() => setMarking(e)} title="Mark invoiced" data-testid={`mark-${e.id}`}
                              className="text-slate-300 hover:text-emerald-300 p-1.5"><FileText size={14} /></button>
                    )}
                    <button onClick={() => onDelete(e)} title="Delete"
                            className="text-slate-300 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!resp.data.length && (
                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500">No entries match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {marking && <InvoiceModal entry={marking} onClose={() => setMarking(null)}
                                onDone={() => { setMarking(null); load(); }} api={api} />}
    </div>
  );
}

function InvoiceModal({ entry, onClose, onDone, api }) {
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!num.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/diary/${entry.id}/invoice`, { invoice_number: num.trim() });
      toast.success(`Marked invoiced #${num}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0b1020] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Mark as invoiced</h3>
          <button onClick={onClose}><X size={16} className="text-slate-400" /></button>
        </div>
        <div className="text-xs text-slate-400 mb-3">
          {entry.client_name} · {entry.task} · {entry.work_date}
        </div>
        <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Invoice number</label>
        <input value={num} onChange={e => setNum(e.target.value)} autoFocus data-testid="invoice-num"
               placeholder="e.g. INV-2026-001"
               className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-slate-300">Cancel</button>
          <button onClick={submit} disabled={busy || !num.trim()} data-testid="invoice-save"
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-1.5">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
